/* Site-wide promo for the September course group.

   FORMAT IS AN SEO CONSTRAINT, NOT A DESIGN PREFERENCE. Google demotes pages
   whose mobile content is covered by an "intrusive interstitial", so this is
   never a full-screen overlay: a small card bottom-right on desktop, a bar
   pinned to the bottom on mobile that is capped at 25% of the viewport
   height. Do not turn it into a modal.

   The rest of the rules:
     - appears after OFFER.popupDelayMs, or at OFFER.popupScrollRatio of the
       page, whichever comes first
     - once per session (sessionStorage, OFFER.popupKey); dismissing it is
       remembered, so it never comes back in the same session
     - the close button is a real 44x44 target with an aria-label
     - it carries ONE offer - the basic course - never a list
     - it stays away from the pages that already sell it (/ponude,
       /educations, in every language)
     - no slide-in under prefers-reduced-motion */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BRAND, GOLD, INK, BODY, serif, sans } from './blocks';
import { BASIC_COURSE } from '../../config/educations.mjs';
import { OFFER } from '../../config/offers.mjs';
import { routeForSlug, linkTo } from '../../i18n/lang.mjs';

/* Logical routes that already carry the offer in full. routeForSlug() maps
   /offers and /angebote back onto '/ponude', so one entry covers all three
   language versions of each. */
const SILENT_ROUTES = ['/ponude', '/educations'];

const styles = `
  /* MOBILE - a bar across the bottom. The 25vh cap is the hard rule; the
     copy is trimmed to stay comfortably under it, so the cap never has to do
     the cutting itself. The right padding keeps text clear of the 44px close
     target sitting in the corner. */
  .vb-offer-pop {
    position: fixed;
    z-index: 9998;
    right: 0; bottom: 0; left: 0;
    max-height: 25vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 48px 12px 16px;
    background: #fff;
    border-top: 3px solid ${BRAND};
    box-shadow: 0 -8px 32px rgba(26,13,5,0.16);
    animation: vbOfferIn 0.35s ease-out both;
  }
  .vb-offer-pop-text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .vb-offer-pop-label { display: none; }
  .vb-offer-pop-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }

  /* DESKTOP - a card in the bottom-right corner, out of the content's way. */
  @media (min-width: 768px) {
    .vb-offer-pop {
      left: auto;
      right: 24px;
      bottom: 24px;
      width: 360px;
      max-height: none;
      gap: 12px;
      padding: 22px 24px 24px;
      border: 1px solid rgba(144,81,15,0.16);
      border-top: 3px solid ${BRAND};
      border-radius: 20px;
    }
    .vb-offer-pop-text { -webkit-line-clamp: 4; }
    .vb-offer-pop-label { display: block; }
  }

  @keyframes vbOfferIn {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @media (prefers-reduced-motion: reduce) {
    .vb-offer-pop { animation: none; }
  }
`;

const pillStyle = (variant) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '11px 16px',
  background: variant === 'solid' ? BRAND : 'transparent',
  color: variant === 'solid' ? '#fff' : BRAND,
  border: variant === 'solid' ? '1px solid transparent' : `1.5px solid ${BRAND}`,
  fontFamily: sans,
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  borderRadius: '9999px',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
});

const OfferPopup = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  const lang = ['sr', 'en', 'de'].includes(i18n.language) ? i18n.language : 'sr';
  // useLocation() is basename-relative, so this is already the logical route.
  const route = routeForSlug(pathname);
  const silent = SILENT_ROUTES.includes(route);

  useEffect(() => {
    if (silent) return undefined;
    if (typeof window === 'undefined') return undefined;

    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(OFFER.popupKey) === '1';
    } catch (err) {
      // Private mode / storage blocked - treat it as "not shown yet".
    }
    if (dismissed) return undefined;

    const show = () => setVisible(true);

    const timer = window.setTimeout(show, OFFER.popupDelayMs);
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      if (window.scrollY / scrollable >= OFFER.popupScrollRatio) show();
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [silent, pathname]);

  const dismiss = () => {
    setVisible(false);
    try {
      window.sessionStorage.setItem(OFFER.popupKey, '1');
    } catch (err) {
      // Nothing to do - it will simply reappear on the next page view.
    }
  };

  if (silent || !visible) return null;

  const startInline = BASIC_COURSE.startDateInline[lang] || BASIC_COURSE.startDateInline.sr;

  return (
    <>
      <style>{styles}</style>
      <aside className="vb-offer-pop" aria-label={t('offerPopup.label')}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p
            className="vb-offer-pop-label"
            style={{
              fontFamily: sans,
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: GOLD,
              margin: '0 0 6px',
            }}
          >
            {t('offerPopup.label')}
          </p>
          <p
            style={{
              fontFamily: serif,
              fontSize: '19px',
              fontWeight: 600,
              lineHeight: 1.25,
              color: INK,
              margin: 0,
            }}
          >
            {t('offerPopup.title', { max: BASIC_COURSE.maxAttendees })}
          </p>
          <p
            className="vb-offer-pop-text"
            style={{
              fontFamily: sans,
              fontSize: '13px',
              lineHeight: 1.5,
              color: BODY,
              margin: '4px 0 0',
            }}
          >
            {t('offerPopup.text', { startInline })}
          </p>
        </div>

        <div className="vb-offer-pop-actions">
          <Link to={linkTo('/ponude', lang)} style={pillStyle('solid')} onClick={dismiss}>
            {t('offerPopup.ctaOffer')}
          </Link>
          <Link to="/educations" style={pillStyle('outline')} onClick={dismiss}>
            {t('offerPopup.ctaApply')}
          </Link>
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label={t('offerPopup.close')}
          style={{
            position: 'absolute',
            top: '2px',
            right: '2px',
            width: '44px',
            height: '44px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            color: BODY,
            cursor: 'pointer',
            lineHeight: 0,
            padding: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </aside>
    </>
  );
};

export default OfferPopup;
